import React from 'react';
import { ArrowRight, Check, Users, Bot, LineChart, Zap } from 'lucide-react';

interface CRMHeroProps {
  onStartTrial: () => void;
}

export default function CRMHero({ onStartTrial }: CRMHeroProps) {
  const badges = [
    {
      icon: Users,
      label: 'Smart Lead Management',
      value: 'AI-Powered'
    },
    {
      icon: Bot,
      label: 'Automated Support',
      value: '24/7'
    },
    {
      icon: LineChart,
      label: 'Sales Analytics',
      value: 'Real-time'
    },
    {
      icon: Zap,
      label: 'Task Automation',
      value: '90% Faster'
    }
  ];

  return (
    <div className="relative bg-white dark:bg-gray-900">
      <div className="relative pt-16 pb-24 lg:pt-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <h1 className="mb-8">
                <span className="block text-base font-semibold text-purple-600 mb-2">
                  Introducing
                </span>
                <span className="mt-1 block text-4xl tracking-tight font-bold sm:text-5xl xl:text-6xl">
                  <span className="block text-gray-900 dark:text-white mb-2">Pino AI CRM</span>
                  <span className="block text-purple-600">Smart Business Growth</span>
                </span>
              </h1>
              <p className="mt-3 text-base text-gray-500 dark:text-gray-400 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                Transform your business with our all-in-one CRM solution. Automate your marketing, 
                streamline sales, and deliver exceptional customer experiences.
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                {badges.map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <div key={badge.label} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                          <Icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {badge.value}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {badge.label}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8">
                <button
                  onClick={onStartTrial}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
              <div className="relative mx-auto w-full rounded-lg shadow-lg lg:max-w-md">
                <div className="relative block w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                  <img
                    className="w-full"
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
                    alt="Pino AI CRM Dashboard"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/50 to-blue-600/50 mix-blend-multiply" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}